import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/** Handles the Emergent Google OAuth callback: reads `session_id` from
 *  the URL fragment (#session_id=...), exchanges it for a backend session,
 *  then redirects to ?next=...  */
export default function AuthCallback() {
  const { loginWithGoogleSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const exchangedRef = useRef(false);

  useEffect(() => {
    if (exchangedRef.current) return;
    exchangedRef.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    const next = new URLSearchParams(location.search).get("next") || "/profile";

    if (!sessionId) {
      toast.error("No session returned from Google. Please try again.");
      navigate("/login");
      return;
    }

    (async () => {
      try {
        await loginWithGoogleSession(sessionId);
        toast.success("Signed in with Google");
        window.history.replaceState(null, "", window.location.pathname);
        navigate(next);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Google sign-in failed");
        navigate("/login");
      }
    })();
  }, [loginWithGoogleSession, navigate, location.search]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-callback">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-blue mx-auto mb-3" />
        <p className="text-slate-600">Signing you in…</p>
      </div>
    </div>
  );
}
